import { useMemo } from "react"

import { usePublicBoundlessConfigRetrieveSuspense } from "@boundless/bl-api-client/public/services"
import { useMatchRoute, useNavigate } from "@tanstack/react-router"
import { ExternalLinkIcon, LogOutIcon, MoreHorizontalIcon } from "lucide-react"

import {
  CompanySwitcherMenuItems,
  useCompanySwitcher,
} from "@/components/layout/CompanySwitcher"
import {
  type DashboardNavItem,
  isInternalNavItem,
  useDashboardNavigation,
} from "@/components/layout/dashboard-navigation"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"

interface Props {
  onLogout: () => void
}

export function MobileBottomNav({ onLogout }: Props) {
  const navigate = useNavigate()
  const matchRoute = useMatchRoute()
  const { data: boundlessConfig } = usePublicBoundlessConfigRetrieveSuspense()
  const { topItems, bottomItems } = useDashboardNavigation()
  const switcher = useCompanySwitcher()

  const primaryItems = useMemo(
    () =>
      topItems.filter(
        (item) => item.mobilePrimary && isInternalNavItem(item),
      ) as DashboardNavItem[],
    [topItems],
  )

  const moreItems = useMemo(() => {
    const seen = new Set<string>()
    return [
      ...primaryItems.slice(5),
      ...topItems.filter((item) => !item.mobilePrimary),
      ...bottomItems,
    ].filter((item) => {
      if (seen.has(item.id)) {
        return false
      }
      seen.add(item.id)
      return true
    })
  }, [bottomItems, primaryItems, topItems])

  if (primaryItems.length === 0 && moreItems.length === 0) {
    return null
  }

  const openNavItem = (item: DashboardNavItem) => {
    if (item.disabled) {
      return
    }

    if (isInternalNavItem(item)) {
      navigate(item.navLinkOptions)
      return
    }

    if (item.newTab) {
      window.open(item.href, "_blank", "noopener,noreferrer")
      return
    }

    window.location.href = item.href
  }

  const isItemActive = (item: DashboardNavItem) => {
    if (!isInternalNavItem(item) || item.disabled) {
      return false
    }

    return Boolean(matchRoute({ ...item.navLinkOptions, fuzzy: true }))
  }

  return (
    <nav
      aria-label="Dashboard mobile navigation"
      className="fixed inset-x-0 bottom-0 z-[80] border-t bg-white/95 px-2 pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] backdrop-blur lg:hidden"
    >
      <div className="grid w-full grid-cols-6 gap-1">
        {primaryItems.slice(0, 5).map((item) => {
          const Icon = item.icon
          const active = isItemActive(item)

          return (
            <button
              key={item.id}
              type="button"
              aria-current={active ? "page" : undefined}
              disabled={item.disabled}
              onClick={() => openNavItem(item)}
              className={cn(
                "flex min-h-14 flex-col items-center justify-center gap-1 rounded-md px-1 py-1 text-[10px] font-medium transition-colors min-[360px]:text-[11px]",
                active
                  ? "text-primary bg-primary/10"
                  : "text-muted-foreground hover:bg-muted/20",
                item.disabled && "cursor-not-allowed opacity-50",
              )}
            >
              <Icon className="size-4" />
              <span className="block w-full truncate text-center leading-tight">
                {item.mobileLabel ?? item.name}
              </span>
            </button>
          )
        })}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="text-muted-foreground hover:bg-muted/20 flex min-h-14 flex-col items-center justify-center gap-1 rounded-md px-1 py-1 text-[10px] font-medium transition-colors min-[360px]:text-[11px]"
            >
              <MoreHorizontalIcon className="size-4" />
              <span>More</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="top" className="mb-2 w-64">
            {switcher && (
              <>
                <CompanySwitcherMenuItems {...switcher} />
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuLabel>More</DropdownMenuLabel>
            {moreItems.map((item) => {
              const Icon = item.icon

              return (
                <DropdownMenuItem
                  key={item.id}
                  disabled={item.disabled}
                  onSelect={() => {
                    openNavItem(item)
                  }}
                >
                  <Icon className="size-4" />
                  {item.name}
                  {!isInternalNavItem(item) && (
                    <ExternalLinkIcon className="text-muted-foreground ml-auto size-3" />
                  )}
                </DropdownMenuItem>
              )
            })}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => {
                window.open(
                  boundlessConfig.bookACallUrl,
                  "_blank",
                  "noopener,noreferrer",
                )
              }}
            >
              <ExternalLinkIcon className="size-4" />
              Book a Call
            </DropdownMenuItem>
            <DropdownMenuItem
              variant="destructive"
              onSelect={() => {
                onLogout()
              }}
            >
              <LogOutIcon className="size-4" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </nav>
  )
}
